import { omit, isEmpty } from 'lodash';
import { normalize, schema } from 'normalizr';
import { toast } from 'react-toastify';
import Immutable from 'seamless-immutable';

import api from '~/api/pipelines';
import { ALL_PROJECTS_ID } from '~/common/helpers/constants';
import i18n from '~/common/helpers/i18n';

const normalizeData = ({ entity, data }) => {
  const pipelinesSchema = new schema.Entity(entity, {}, { idAttribute: '_id' });
  const mySchema = [pipelinesSchema];
  return normalize(data, mySchema);
};

const onUniqId = (allIds, entities) => {
  const content = entities?.content?.filter(
    // eslint-disable-next-line no-underscore-dangle
    entity => !allIds?.includes(entity._id)
  );
  return {
    ...entities,
    content
  };
};

const initialState = Immutable({
  result: {
    pipelines: null
  },
  entities: {
    pipelines: []
  },
  loading: {
    pipelines: 0,
    upgradePipelineVersion: false,
    pagination: false,
    search: false
  },
  errors: {
    pipelines: null
  },
  update: {
    upgradePipelineVersion: null
  },
  last: false,
  first: true,
  searchParams: {
    name: '',
    disabledHistory: false,
    page: 0,
    projectId: null
  },
  numberOfElements: 0,
  number: 0,
  size: 0,
  totalElements: 0
});

const pipelines = {
  name: 'pipelines',
  state: initialState,
  reducers: {
    setResultAndEntities(state, { result, entities, ...props }) {
      return state.merge({
        result: { pipelines: result },
        entities,
        errors: { pipeline: null },
        totalElements: props.totalElements,
        numberOfElements: props.numberOfElements,
        number: props.number,
        size: props.size,
        first: props.first,
        last: props.last
      });
    },
    setError(state, error) {
      return state.merge({ errors: { pipelines: error } });
    },
    setLoading(state, { path, value }) {
      if (path === 'pipelines') {
        const currentValue = state.loading.pipelines;
        const newValue = value
          ? currentValue + 1
          : Math.max(0, currentValue - 1);

        return state.merge(
          { loading: { pipelines: newValue } },
          { deep: true }
        );
      }

      return state.merge({ loading: { [path]: value } }, { deep: true });
    },
    setSearch(state, payload) {
      return state.merge({ searchParams: payload });
    },
    removeResult(state, payload) {
      return state.merge(
        {
          result: {
            pipelines: payload
          }
        },
        { deep: true }
      );
    },
    setUpdate(state, { path, value }) {
      return state.merge(
        {
          update: {
            [path]: value
          }
        },
        { deep: true }
      );
    },
    clear: () => initialState,
    patchPipelineEntity(state, { id, patch }) {
      const pipelinesById = state.entities.pipelines || {};
      const entryKey = Object.keys(pipelinesById).find(key => {
        const entity = pipelinesById[key];
        // eslint-disable-next-line no-underscore-dangle
        return key === id || entity?._id === id || entity?.id === id;
      });
      if (!entryKey) {
        return state;
      }
      return state.merge(
        {
          entities: {
            pipelines: {
              [entryKey]: { ...pipelinesById[entryKey], ...patch }
            }
          }
        },
        { deep: true }
      );
    }
  },
  effects: dispatch => ({
    async getAll(
      context = { search: {} },
      { pipelines: pipelinesState, application, project }
    ) {
      try {
        const { realm } = application.realm;
        const params = !isEmpty(context.search)
          ? context.search
          : pipelinesState.searchParams;

        let projectId;
        projectId = params.projectId ? params.projectId : project?.current;
        projectId = projectId === ALL_PROJECTS_ID ? null : projectId;

        const searchPayload = {
          ...params,
          projectId
        };

        const listPipelines = await api.fetch({
          realm,
          search: searchPayload
        });

        const result = listPipelines.data.searchPipelines;
        const pipelineNormalize = normalizeData({
          entity: 'pipelines',
          data: result.content
        });

        dispatch.pipelines.setResultAndEntities(
          {
            ...pipelineNormalize,
            ...omit(result, ['content'])
          },
          context
        );
      } catch (e) {
        dispatch.pipelines.setError({ error: { pipeline: e.message } });
      }
    },
    cancel: () => api.cancel(),
    async getPage({ page, projectId }, state) {
      try {
        const { realm } = state.application.realm;
        const searchParams = {
          ...state?.pipelines?.searchParams,
          page,
          projectId: projectId !== ALL_PROJECTS_ID ? projectId : null
        };

        const listPipelines = await api.fetch({
          realm,
          search: searchParams
        });
        const pipelinesNew = onUniqId(
          state?.pipelines?.result?.pipelines,
          listPipelines?.data?.searchPipelines
        );

        const pipelineNormalize = normalizeData({
          entity: 'pipelines',
          data: pipelinesNew.content
        });

        const result = [
          ...state.pipelines.asMutable({ deep: true }).result.pipelines,
          ...pipelineNormalize.result
        ];
        const entities = {
          ...state.pipelines.asMutable({ deep: true }).entities.pipelines,
          ...pipelineNormalize.entities.pipelines
        };
        const pipelinesData = {
          result,
          entities: {
            pipelines: entities
          }
        };

        dispatch.pipelines.setResultAndEntities({
          ...pipelinesData,
          ...omit(listPipelines.data.searchPipelines, ['content'])
        });
      } catch (e) {
        dispatch.pipelines.setError({ error: { pipeline: e.message } });
      }
    },
    async upgradePipelineVersion(params, { application }) {
      try {
        const { realm } = application.realm;
        const { data } = await api.upgradePipelineVersion({ ...params, realm });
        dispatch.pipelines.setUpdate({
          path: 'upgradePipelineVersion',
          value: data
        });

        const pipelineFolderId =
          typeof params.folder === 'string' ? params.folder : undefined;
        const urlParams = new URLSearchParams();
        if (pipelineFolderId) {
          urlParams.set('folderId', pipelineFolderId);
        }
        const queryString = urlParams.toString();
        const url = `/${realm}/design/pipelines/${data.upgradePipelineVersion}${
          queryString ? `?${queryString}` : ''
        }`;
        dispatch.router.navigate({
          to: url
        });
      } catch (e) {
        toast.error(
          `${i18n.t(
            'scenes.pipelines.labels.upper_and_capitalize.capitalized',
            {
              item: `${i18n.t('scenes.pipelines.messages.error.new_item')}`
            }
          )}`
        );
      }
    }
  }),
  logics: [
    {
      type: 'pipelines/getAll',
      latest: true,
      process({ action }, dispatch, done) {
        const { payload, meta } = action;
        if (payload?.search) {
          dispatch.project.setCurrent(payload?.search?.projectId);
        }

        dispatch.pipelines.setLoading({
          path: meta?.type ? meta.type : 'pipelines',
          value: true
        });
        done();
      }
    },
    {
      type: ['pipelines/getPage'],
      latest: true,
      process(context, dispatch, done) {
        dispatch.pipelines.setLoading({ path: 'pagination', value: true });
        done();
      }
    },
    {
      type: ['pipelines/setResultAndEntities', 'pipelines/setError'],
      latest: true,
      process({ action }, dispatch, done) {
        const { meta } = action;
        dispatch.pipelines.setLoading({ path: 'pipelines', value: false });
        dispatch.pipelines.setLoading({ path: 'search', value: false });
        dispatch.pipelines.setLoading({ path: 'pagination', value: false });
        if (!isEmpty(meta?.search)) {
          dispatch.pipelines.setSearch(meta.search);
        }
        done();
      }
    },
    {
      type: ['pipelines/upgradePipelineVersion'],
      latest: true,
      process(context, dispatch, done) {
        dispatch.pipelines.setLoading({
          path: 'upgradePipelineVersion',
          value: false
        });
        done();
      }
    },
    {
      type: 'pipelines/setUpdate',
      latest: true,
      process(context, dispatch, done) {
        dispatch.pipelines.setLoading({
          path: 'upgradePipelineVersion',
          value: true
        });
        done();
      }
    }
  ]
};

export default pipelines;

// 'profile/save'
